<template>
  <div>
    <cms-section
      :class-wrapper="isHidden ? '!hidden' : ''"
      disable-lazy-hydrate
      :first-section="firstElement"
      :page-context
      :section="{ name: flagKey, items: allVariants }"
    />
    <cms-section
      :class="{ skeleton: showSkeleton }"
      :class-wrapper="[isHidden ? '' : '!hidden', showSkeleton ? 'invisible' : ''].filter(Boolean).join(' ')"
      disable-lazy-hydrate
      :first-section="firstElement"
      :page-context
      :section="{ name: flagKey, items: allBaselines }"
    />
  </div>
</template>

<script lang="ts" setup>
import type { Experiment } from '#types/components/cms/experiment'
import mappings from '#content/mappings'

const { content, firstElement, pageContext } = defineProps<{
  content: Experiment
  firstElement?: boolean
  pageContext?: boolean
}>()

const { decide, decisions } = useOptimizely()
const { $feature, $gtm, $viewport } = useNuxtApp()
const route = useRoute()
const auth = useAuthStore()
const cart = useCartStore()

const mapTargets = (targets, flagKey: string) =>
  targets?.map((target) => {
    const resolved = resolveContentComponent(target, mappings)
    if (!resolved) return null

    return {
      ...resolved,
      ...(content.menuId !== undefined && { menuId: content.menuId }),
      ...(content.renderMode !== undefined && { renderMode: content.renderMode }),
      ...(content.tabIndex !== undefined && { tabIndex: content.tabIndex }),
      dataTestVariant: flagKey
    }
  })

const getVariantKey = (key: string): string => (key.match(/\d+-(.+)/) || [])[1] || ''
const getVariantId = (text: string): string => (text.match(/^(\d+)-/) || [])[1] || ''
const getForcedVariantId = (text: string): string => (text.match(/vfcanvas-optimizely_variant_(\d+)/) || [])[1] || ''
const isHidden = ref(true)
const showSkeleton = ref(true)

const flagKey = content.experienceId?.match(/optimizely_experience_(.+)$/)?.[1] || ''
const cmsDecideOptions = getOptimizelyCmsCmabDecideOptions(content.experienceType, content.attribute_keys)

/** True when at least one variant target would render at the current breakpoint. */
const hasVisibleVariantContent = computed(() =>
  (content.variants ?? []).some(({ targets }) =>
    (targets ?? []).some(({ hideOnBreakpoint }) => !hideOnBreakpoint?.[$viewport.breakpoint])
  )
)

// Prefetch only when some variant can show — impression tracked in revaluateDecision on mount.
if ($feature.configP13n && hasVisibleVariantContent.value) {
  decide(flagKey, { ...cmsDecideOptions, disableDecisionEvent: true })
}

const allVariants = ref(
  (content.variants ?? []).map((variant) =>
    mapTargets(
      variant.targets ?? [],
      `${flagKey}-${getVariantKey(variant.variantId)}`
    ) ?? []
  ).flat(2).filter(Boolean)
)

const allBaselines = ref(mapTargets(content.baselines, `baseline-${flagKey}`)?.flat())

const forcedVariants = route.query.p13n_testcontext ? JSON.parse(route.query.p13n_testcontext as string)?.variants : []
const forcedVariantsIds = forcedVariants.map((variant) => getForcedVariantId(variant))

const getRevaluatedVariants = () => forcedVariantsIds?.length
  ? content.variants.find(
    ({ variantId }) => forcedVariantsIds.includes(getVariantId(variantId))
  )?.targets || []
  : content.variants.find(
    ({ variantId }) => getVariantKey(variantId) === decisions.value.get(flagKey)?.variationKey
  )?.targets || []

let lastPushedVariationKey: string | null | undefined

const revaluateDecision = async () => {
  if (!($feature.configP13n && flagKey)) return

  // No variant can show at this breakpoint — skip Opti decide/tracking entirely.
  if (!hasVisibleVariantContent.value) {
    showSkeleton.value = false
    return
  }

  await decide(flagKey, cmsDecideOptions)
  const decision = decisions.value.get(flagKey)
  // Only push to GA when the resolved variation actually changes for this
  // component instance. The watcher below re-decides on route/auth/cart changes.
  if (decision && decision.variationKey !== lastPushedVariationKey) {
    lastPushedVariationKey = decision.variationKey
    $gtm.push('optimizely.onOptimizelyDecision', { ...decision, contentId: content.id })
  }
  const chosenTargets = getRevaluatedVariants()

  allVariants.value = mapTargets(
    chosenTargets,
    `${flagKey}-${decisions.value.get(flagKey)?.variationKey}`
  )?.flat() || []
  log.info(`Optimizely variation chosen for component: ${content.id} was ${decisions.value.get(flagKey)?.variationKey}`)
  isHidden.value = allVariants.value.length === 0
  showSkeleton.value = false
}

// Redecide when targeting inputs or breakpoint visibility of variant content change.
watch(() => [route.fullPath, auth.loggedIn, cart.items, $viewport.breakpoint], revaluateDecision)

onMounted(revaluateDecision)
</script>
